import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { PermAction, PermOptions, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { evaluatePermissions } from "@/types/base/authuser"
import { Role } from "@/types/base/authuser"
import { TableType } from "@/types/base/enums"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.chats')

export const getChatById = async (supabase: SupabaseClient<Database>, chatId: string) => {
  const { data: chat, error } = await supabase
    .from("chats")
    .select("*")
    .eq("id", chatId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Chat by ID', { chatId })
    throw new Error(error.message)
  }

  return chat
}

export const getOptionalChatByName = async (supabase: SupabaseClient<Database>, chatName: string) => {
  const { data: chat, error } = await supabase
    .from("chats")
    .select("*")
    .eq("name", chatName)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch optional Chat by name', { chatName })
    throw new Error(error.message)
  }

  return chat
}

export const getChatsByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: chats, error } = await supabase
    .from("chats")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })

  if (!chats) {
		logError(logger, error, 'Failed to fetch Chats by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

  return chats
}

export const getChatsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'chats'>[]> => {
	const wsChats = await getChatsByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'chats'>[] = []

	for (const chat of wsChats) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "chats", context: "ANY", uuid: chat.id, ws: workspace})

		result.push({ ...chat, permissions: perms })
	}

  return result
}

export const getChatMessagesByChatId = async (
	supabase: SupabaseClient<Database>,
  chatId: string
) => {
	const { data, error } = await supabase
		.from('chats')
		.select(`
			*,
			messages (*)
		`)
    .eq("id", chatId)
    .order("sequence_number", { foreignTable: 'messages' })

	if (error) {
		logError(logger, error, 'Failed to fetch Chat messages by chat ID', { chatId })
		throw new Error(error.message)
	}

	return data.map(({ messages, ...chat }) => ({
		...chat,
		messages: messages.map(({ text_search_vec, lang, ...message }) => message)
	}))
}

export const getChatMessagesByWorkspaceId = async (
	supabase: SupabaseClient<Database>,
  workspaceId: string
) => {
	const { data, error } = await supabase
		.from('chats')
		.select(`
			*,
			messages (*)
		`)
    .eq("workspace_id", workspaceId)
    .order("sequence_number", { foreignTable: 'messages' })

	if (error) {
		logError(logger, error, 'Failed to fetch Chat messages by workspace ID', { workspaceId })
		throw new Error(error.message)
	}

	return data.map(({ messages, ...chat }) => ({
		...chat,
		messages: messages.map(({ text_search_vec, lang, ...message }) => message)
	}))
}

export const createChat = async (supabase: SupabaseClient<Database>, chat: TablesInsert<"chats">) => {
  const { data: createdChat, error } = await supabase
    .from("chats")
    .insert([chat])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Chat', { chat: printObject(chat) })
    throw new Error(error.message)
  }

  logger.info('Chat created successfully', { createdChat: printObject(createdChat) })
  return createdChat
}

export const createChats = async (supabase: SupabaseClient<Database>, chats: TablesInsert<"chats">[]) => {
  const { data: createdChats, error } = await supabase
    .from("chats")
    .insert(chats)
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create multiple Chats', { chats: printObject(chats) })
    throw new Error(error.message)
  }

  logger.info('Chats created successfully', { createdChats: printObject(createdChats) })
  return createdChats
}

export const updateChat = async (
	supabase: SupabaseClient<Database>,
  chatId: string,
  chat: TablesUpdate<"chats">
) => {
  const { data: updatedChat, error } = await supabase
    .from("chats")
    .update(chat)
    .eq("id", chatId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update chat', { chatId, chat: printObject(chat) })
    throw new Error(error.message)
  }

  return updatedChat
}

export const deleteChat = async (supabase: SupabaseClient<Database>, chatId: string) => {
  const { error } = await supabase.from("chats").delete().eq("id", chatId)

  if (error) {
		logError(logger, error, 'Failed to delete Chat', { chatId })
    throw new Error(error.message)
  }

  return true
}

export const chatCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.ws && authuser.isOwnWorkspace(options.ws)) {
		return true
	}

	switch (options.action) {
		case PermAction.view:
			if (options.uuid) {
				return await authuser.isAuthorizedForTable(supabase, TableType.chat, options.uuid, Role.viewer)
			}
			return false

		case PermAction.create:
			if (options.ws) {
				return authuser.isAuthorizedForRole(Role.user) &&
						authuser.isAuthorizedForTable(supabase, TableType.workspace, options.ws.id, Role.user)
			}
			return false

		case PermAction.delete:
			if (options.ws && options.context == 'Folder') {
				return authuser.isAuthorizedForRole(Role.user) &&
						authuser.isAuthorizedForTable(supabase, TableType.workspace, options.ws.id, Role.user)
			}

			if (options.uuid && options.ws) {
				try {
					const chatData = await getChatById(supabase, options.uuid)
					if (chatData?.user_id == options.ws.user_id) {  //own chats
						return true
					}
					else if (await authuser.isAuthorizedForTable(supabase, TableType.chat, options.uuid, Role.powerUser)) {
						return true
					}
				} catch (error) {  //if not found we'll let it go
					logger.info("Chat not found for id:" + options.uuid) //false
				}
				//false
			}
			return false

		case PermAction.drag:
		case PermAction.edit:
		case PermAction.update:
		case PermAction.save:
			if (options.ws && options.context == 'Folder') {
				return authuser.isAuthorizedForRole(Role.user) &&
						authuser.isAuthorizedForTable(supabase, TableType.workspace, options.ws.id, Role.user)
			}
			if (options.uuid) {
				return await authuser.isAuthorizedForTable(supabase, TableType.chat, options.uuid, Role.user)
			}
			return false
		default:
			return false  //for now not used, so don't allow
	}
}
